import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Loader2, Check } from "lucide-react";
import Reveal from "@/components/site/Reveal";
import ContactMotif from "@/components/site/ContactMotif";
import PageSeo from "@/components/site/PageSeo";
import ContactChannels from "@/components/site/ContactChannels";
import { CONTACT } from "@/constants/testIds";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useWebsiteContent } from "@/hooks/useWebsiteContent";
import { trackGenerateLead } from "@/lib/ga";

const API = "/api";

export default function Contact() {
  const { data: site } = useSiteSettings();
  const { data: wc }   = useWebsiteContent();
  const topics = (wc?.contactTopics || []).map((t) => t.label).filter(Boolean);

  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    country: "",
    topic: "",
    message: "",
  });
  const [source, setSource] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  // Context handoff: /contact?topic=<label>&source=<path> preselects the
  // topic (when it matches an option) and attributes the enquiry source.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const src = params.get("source") || "";
    if (src) setSource(src.slice(0, 300));
    const t = (params.get("topic") || "").trim();
    if (!t || topics.length === 0) return;
    const match = topics.find((o) => o.toLowerCase() === t.toLowerCase());
    if (match) setForm((f) => (f.topic ? f : { ...f, topic: match }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topics.length]);

  const successRef = useRef(null);

  // After a successful submission, smoothly scroll the "Message sent"
  // card into view and move keyboard focus to it. Falls back to instant
  // scroll when the user has prefers-reduced-motion enabled.
  useEffect(() => {
    if (!submitted) return;
    const el = successRef.current;
    if (!el) return;
    const t = window.setTimeout(() => {
      const supportsSmooth =
        typeof CSS !== "undefined" && CSS.supports && CSS.supports("scroll-behavior", "smooth");
      const behavior = window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : supportsSmooth
        ? "smooth"
        : "auto";
      el.scrollIntoView({ behavior, block: "start" });
      el.focus({ preventScroll: true });
    }, 60);
    return () => window.clearTimeout(t);
  }, [submitted]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        country: form.country.trim() || null,
        topic: form.topic || null,
        message: form.message.trim(),
        source: source || null,
      };
      await axios.post(`${API}/contact`, payload);
      trackGenerateLead({
        topic: payload.topic || "none",
        source: payload.source || "direct",
        country: payload.country || "unknown",
        method: "contact_form",
      });
      setSubmitted(true);
      setForm({ name: "", email: "", company: "", country: "", topic: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "We couldn't submit your message. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-[#d2d2d7] py-3 text-[15px] text-[#1D1D1F] placeholder:text-[#86868b] focus:outline-none focus:border-[#1D1D1F] transition-colors";

  return (
    <main data-testid={CONTACT.root}>
      <PageSeo
        path="/contact"
        title="Contact — Start a project"
        description="Tell us about your Asia sourcing, manufacturing or trading project. Reach us via email, WhatsApp or LinkedIn. Typical reply within one business day."
        schemas={[
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home",
                item: typeof window !== "undefined" ? window.location.origin + "/" : "/" },
              { "@type": "ListItem", position: 2, name: "Contact" },
            ],
          },
          // ContactPage schema — signals to search engines and AI assistants
          // that this URL is the canonical contact page for the Organization.
          // mainEntity references the site-wide Organization node emitted by
          // SiteSchemas.jsx via its @id (contact channels themselves live in
          // Organization.contactPoint on that node — no duplication here).
          {
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: "Contact Manufacturing and Trading Services (MTS)",
            url: (typeof window !== "undefined" ? window.location.origin : "") + "/contact",
            description: "Tell us about your Asia sourcing, manufacturing or trading project. Reach us via email, WhatsApp or LinkedIn. Typical reply within one business day.",
            isPartOf: { "@id": (typeof window !== "undefined" ? window.location.origin : "") + "/#website" },
            mainEntity: { "@id": (typeof window !== "undefined" ? window.location.origin : "") + "/#organization" },
          },
        ]}
      />
      {/* HEADER ========================================================== */}
      <section className="border-b border-[#ececef] mts-soft-gradient relative overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 mts-dot-grid opacity-30 [mask-image:radial-gradient(60%_60%_at_50%_50%,black,transparent)]"
        />
        {/* Contact motif — subtle branded accent on the right */}
        <div
          aria-hidden
          className="hidden md:block absolute right-0 top-0 h-full w-[46%] pointer-events-none opacity-70"
          style={{
            WebkitMaskImage:
              "linear-gradient(to right, transparent 0%, black 40%, black 100%)",
            maskImage:
              "linear-gradient(to right, transparent 0%, black 40%, black 100%)",
          }}
        >
          <div className="relative h-full w-full flex items-center justify-end">
            <ContactMotif className="w-full h-auto max-h-[440px]" />
          </div>
        </div>
        <div className="relative max-w-[1280px] mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-16 md:pb-24">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#86868b]">
              {wc?.contactEyebrow || ""}
            </p>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="mt-6 text-[44px] sm:text-[60px] md:text-[80px] font-semibold tracking-[-0.03em] leading-[1.02] text-[#0a0a0a] max-w-2xl text-balance">
              {wc?.contactTitlePrefix || ""}{" "}
              <span
                className="italic font-normal"
                style={{ fontFamily: "'Instrument Serif', serif" }}
              >
                {wc?.contactTitleItalic || ""}
              </span>
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-8 text-[17px] md:text-[19px] leading-relaxed text-[#424245] max-w-xl text-pretty">
              {wc?.contactSubtitle || ""}
            </p>
          </Reveal>
        </div>
      </section>

      {/* FORM + CHANNELS ================================================= */}
      <section className="bg-white">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-16">
            {/* Form */}
            <div className="md:col-span-8">
              {submitted ? (
                <div
                  ref={successRef}
                  data-testid={CONTACT.success}
                  tabIndex={-1}
                  aria-live="polite"
                  className="mts-card p-10 md:p-12 bg-[#f7f7f8] scroll-mt-24 focus:outline-none"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-[#0a0a0a] text-white flex items-center justify-center">
                      <Check size={16} />
                    </div>
                    <h2 className="text-[24px] font-medium tracking-tight text-[#0a0a0a]">
                      Message sent.
                    </h2>
                  </div>
                  <p className="mt-4 text-[15px] leading-relaxed text-[#424245] max-w-lg">
                    Your message has been delivered to our team. We'll reply
                    within one business day. If your request is time-sensitive,
                    reach out on WhatsApp using the number on this page.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="mt-8 text-[13px] font-medium text-[#0a0a0a] underline"
                    type="button"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="mts-card p-8 md:p-10 space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <label className="text-[11px] uppercase tracking-[0.15em] text-[#86868b]">
                        Name
                      </label>
                      <input
                        data-testid={CONTACT.name}
                        required
                        value={form.name}
                        onChange={update("name")}
                        className={inputCls}
                        placeholder="Name"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-[0.15em] text-[#86868b]">
                        Email
                      </label>
                      <input
                        data-testid={CONTACT.email}
                        required
                        type="email"
                        value={form.email}
                        onChange={update("email")}
                        className={inputCls}
                        placeholder="you@company.com"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-[0.15em] text-[#86868b]">
                        Company
                      </label>
                      <input
                        data-testid={CONTACT.company}
                        value={form.company}
                        onChange={update("company")}
                        className={inputCls}
                        placeholder="Company name (optional)"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-[0.15em] text-[#86868b]">
                        Country
                      </label>
                      <input
                        data-testid="contact-input-country"
                        value={form.country}
                        onChange={update("country")}
                        className={inputCls}
                        placeholder="Your country (optional)"
                        maxLength={80}
                      />
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-[0.15em] text-[#86868b]">
                        Topic
                      </label>
                      <select
                        data-testid={CONTACT.topic}
                        value={form.topic}
                        onChange={update("topic")}
                        className={`${inputCls} appearance-none pr-6`}
                      >
                        <option value="">Select a topic</option>
                        {topics.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="text-[11px] uppercase tracking-[0.15em] text-[#86868b]">
                      Project details
                    </label>
                    <textarea
                      data-testid={CONTACT.message}
                      required
                      rows={5}
                      value={form.message}
                      onChange={update("message")}
                      className={`${inputCls} resize-none`}
                      placeholder="Product, category, target unit cost, quantities, timeline, current status…"
                    />
                  </div>

                  {error && (
                    <p className="text-[13px] text-[#8a1c1c]">{error}</p>
                  )}

                  <div className="pt-2">
                    <button
                      type="submit"
                      data-testid={CONTACT.submit}
                      disabled={submitting}
                      className="mts-btn-primary disabled:opacity-50"
                    >
                      {submitting && <Loader2 size={14} className="animate-spin" />}
                      Send message
                    </button>
                    <p className="mt-4 text-[12px] text-[#86868b]">
                      Your information is used only to respond to your enquiry.
                    </p>
                  </div>
                </form>
              )}
            </div>

            {/* Channels — CMS-driven. Every contact / social channel in
                SiteSettings appears here automatically (WhatsApp, email,
                phones, socialLinks). Adding a new platform in Payload
                requires no code change. Each click fires a single
                `contact_channel_click` GA4 event with the platform slug. */}
            <aside className="md:col-span-4" data-testid={CONTACT.channelEmail /* legacy testid retained on wrapper */}>
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#86868b] mb-6">
                Direct channels
              </p>
              <ContactChannels location="contact-page" />
            </aside>
          </div>
        </div>
      </section>
    </main>
  );
}
